// Copyright 2018 Sorama B.V.

import { WebGLExtensions } from "extensions/WebGLExtensions";
import SpectrogramPlotFragmentShader from "./../../shaders/SpectrogramPlotFragmentShader.shader";
import SpectrogramPlotVertexShader from "./../../shaders/SpectrogramPlotVertexShader.shader";

export class SpectrogramProgram {
    private shaderProgram: WebGLProgram;

    private vertexPositionLocation: number;
    private texturePositionLocation: number;

    public dataTextureLocation: WebGLUniformLocation;
    public colormapTextureLocation: WebGLUniformLocation;
    public xOriginLocation: WebGLUniformLocation;
    public xWidthLocation: WebGLUniformLocation;

    public yOriginLocation: WebGLUniformLocation;
    public yHeightLocation: WebGLUniformLocation;

    public minimumLocation: WebGLUniformLocation;
    public maximumLocation: WebGLUniformLocation;

    public widthLocation: WebGLUniformLocation;
    public heightLocation: WebGLUniformLocation;

    constructor(gl: WebGLRenderingContext) {
        let error: string;

        // Generate shaders
        const vertexShader = gl.createShader(gl.VERTEX_SHADER);
        const fragmentShader = gl.createShader(gl.FRAGMENT_SHADER);

        // Compile shaders
        gl.shaderSource(vertexShader, SpectrogramPlotVertexShader);
        gl.compileShader(vertexShader);

        if (!gl.getShaderParameter(vertexShader, gl.COMPILE_STATUS)) {
            error = gl.getShaderInfoLog(vertexShader);
            throw new Error(error);
        }

        gl.shaderSource(fragmentShader, SpectrogramPlotFragmentShader);
        gl.compileShader(fragmentShader);

        if (!gl.getShaderParameter(fragmentShader, gl.COMPILE_STATUS)) {
            error = gl.getShaderInfoLog(fragmentShader);
            throw new Error(error);
        }

        // Create program
        this.shaderProgram = gl.createProgram();
        gl.attachShader(this.shaderProgram, vertexShader);
        gl.attachShader(this.shaderProgram, fragmentShader);
        gl.linkProgram(this.shaderProgram);

        if (!gl.getProgramParameter(this.shaderProgram, gl.LINK_STATUS)) {
            error = gl.getProgramInfoLog(this.shaderProgram);
            throw new Error(error);
        }

        gl.useProgram(this.shaderProgram);

        this.vertexPositionLocation = gl.getAttribLocation(this.shaderProgram, "vertexPosition");
        this.texturePositionLocation = gl.getAttribLocation(this.shaderProgram, "texturePosition");

        this.dataTextureLocation = gl.getUniformLocation(this.shaderProgram, "dataTexture");
        this.colormapTextureLocation = gl.getUniformLocation(this.shaderProgram, "colormapTexture");

        this.xOriginLocation = gl.getUniformLocation(this.shaderProgram, "xOrigin");
        this.xWidthLocation = gl.getUniformLocation(this.shaderProgram, "xWidth");
        this.yOriginLocation = gl.getUniformLocation(this.shaderProgram, "yOrigin");
        this.yHeightLocation = gl.getUniformLocation(this.shaderProgram, "yHeight");

        this.minimumLocation = gl.getUniformLocation(this.shaderProgram, "minimum");
        this.maximumLocation = gl.getUniformLocation(this.shaderProgram, "maximum");

        this.widthLocation = gl.getUniformLocation(this.shaderProgram, "width");
        this.heightLocation = gl.getUniformLocation(this.shaderProgram, "height");

        WebGLExtensions.ThrowOnError(gl);
    }

    public enable(gl: WebGLRenderingContext, vertexBuffer: WebGLBuffer, texelBuffer: WebGLBuffer) {
        gl.useProgram(this.shaderProgram);

        // Enable vertex arrays
        gl.enableVertexAttribArray(this.vertexPositionLocation);
        gl.enableVertexAttribArray(this.texturePositionLocation);

        // Update buffer data
        gl.bindBuffer(WebGLRenderingContext.ARRAY_BUFFER, vertexBuffer);
        gl.vertexAttribPointer(this.vertexPositionLocation, 2, WebGLRenderingContext.FLOAT, true, 0, 0);

        gl.bindBuffer(WebGLRenderingContext.ARRAY_BUFFER, texelBuffer);
        gl.vertexAttribPointer(this.texturePositionLocation, 2, WebGLRenderingContext.FLOAT, true, 0, 0);
    }

    public disable(gl: WebGLRenderingContext) {
        gl.disableVertexAttribArray(this.vertexPositionLocation);
        gl.disableVertexAttribArray(this.texturePositionLocation);

        gl.useProgram(null);
    }
}
